import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { fmtDate } from './format';
import { c, t, tbl } from '../../theme';
import { useSort, sortRows, SortTh } from '../Shared/sortable';

// Driver Roster — the truck-type qualification matrix from the source
// workbook's "DRIVER ROSTER" section. Each cell is the DATE a driver
// qualified on that truck type, not a 1-5 score like Mechanic Development,
// so there is no Rating/Expectation/Benchmark here (see migration 115 /
// routes/staffing.js GET+PATCH /drivers).
const DYS_PROGRAM_COLUMNS = [
  { key: 'scout_bys_date', label: 'Scout/BYS' },
  { key: 'driver_trainee_date', label: 'Driver Trainee' },
  { key: 'graduate_date', label: 'Graduate' },
  { key: 'class_b_driver_date', label: 'Class B Driver' },
  { key: 'promotion_date', label: 'Promotion Date' },
];
const TRUCK_QUAL_COLUMNS = [
  { key: 'commercial_front_loader_date', label: 'Commercial Front Loader' },
  { key: 'commercial_rear_loader_date', label: 'Commercial Rear Loader' },
  { key: 'residential_automated_side_loader_date', label: 'Residential Automated Side Loader' },
  { key: 'residential_rear_loader_date', label: 'Residential Rear Loader' },
  { key: 'residential_front_load_curotto_date', label: 'Residential Front Load Curotto' },
  { key: 'residential_recycling_date', label: 'Residential Recycling' },
  { key: 'scout_support_truck_driver_date', label: 'Scout/Support Truck Driver' },
  { key: 'pull_out_date', label: 'Pull Out' },
  { key: 'street_sweeping_date', label: 'Street Sweeping' },
  { key: 'diamond_back_date', label: 'Diamond Back' },
];
const ALL_DATE_COLUMNS = [...DYS_PROGRAM_COLUMNS, ...TRUCK_QUAL_COLUMNS];

export default function DriverQualificationTable({ drivers, canEdit = false }) {
  const showPeople = drivers.some((d) => 'worker_name' in d);
  const sort = useSort();
  const sorted = sortRows(drivers, sort.key, sort.dir, {
    worker_name: (d) => d.worker_name || '',
    job_profile: (d) => d.job_profile || '',
    manager:     (d) => d.manager || '',
  });
  return (
    <div style={s.tableScroll}>
      <table style={s.table}>
        <thead>
          <tr>
            {showPeople && <SortTh label="Driver" sortKey="worker_name" sort={sort} sticky />}
            <SortTh label="Job Profile" sortKey="job_profile" sort={sort} sticky />
            <SortTh label="Supervisor" sortKey="manager" sort={sort} sticky />
            {ALL_DATE_COLUMNS.map((col) => <SortTh key={col.key} label={col.label} sticky />)}
            <SortTh label="Shield Rides" sticky />
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => (
            <DriverRow key={d.worker_employee_id || d.id} driver={d} canEdit={canEdit} showPeople={showPeople} />
          ))}
          {!drivers.length && (
            <tr><td colSpan={showPeople ? ALL_DATE_COLUMNS.length + 4 : ALL_DATE_COLUMNS.length + 3} style={s.empty}>
              No drivers here.
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DriverRow({ driver: d, canEdit, showPeople }) {
  const qc = useQueryClient();
  const [dates, setDates] = useState(() =>
    Object.fromEntries(ALL_DATE_COLUMNS.map((col) => [col.key, d[col.key] ? String(d[col.key]).slice(0, 10) : '']))
  );
  const [shieldRides, setShieldRides] = useState(d.shield_rides ?? '');

  // Re-sync on prop change, not just on mount — see VacancyTable.jsx's
  // VacancyRow for why.
  const dateSignature = ALL_DATE_COLUMNS.map((col) => d[col.key]).join('|');
  useEffect(() => {
    setDates(Object.fromEntries(ALL_DATE_COLUMNS.map((col) => [col.key, d[col.key] ? String(d[col.key]).slice(0, 10) : ''])));
    setShieldRides(d.shield_rides ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateSignature, d.shield_rides]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.patch(`/staffing/drivers/${d.worker_employee_id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staffing-drivers'] });
      qc.invalidateQueries({ queryKey: ['staffing-costcenter'] });
    },
  });

  // Every save upserts the whole row (see PATCH /drivers), so every field
  // not being changed right now has to be re-sent as-is — same reason
  // MechanicSkillTable/VacancyTable/NewHireTable's basePayload() exists.
  function currentPayload(overrides = {}) {
    return { ...dates, shield_rides: shieldRides || null, ...overrides };
  }
  function saveDate(key, value) {
    const next = { ...dates, [key]: value };
    setDates(next);
    saveMutation.mutate(currentPayload({ [key]: value || null }));
  }
  function saveShieldRides() { saveMutation.mutate(currentPayload()); }

  const canEditThisRow = canEdit && !!d.worker_employee_id;

  return (
    <tr style={tbl.tr}>
      {showPeople && (
        <td style={tbl.td}>{d.worker_name}{d.worker_employee_id ? ` (${d.worker_employee_id})` : ''}</td>
      )}
      <td style={s.tdDesc} title={d.job_profile}>{d.job_profile || '—'}</td>
      <td style={tbl.td}>{d.manager || '—'}</td>
      {ALL_DATE_COLUMNS.map((col) => (
        <td key={col.key} style={tbl.td}>
          {canEditThisRow ? (
            <input type="date" style={s.editInput} value={dates[col.key]}
              onChange={(e) => saveDate(col.key, e.target.value)} />
          ) : (fmtDate(d[col.key]))}
        </td>
      ))}
      <td style={s.tdNotes}>
        {canEditThisRow ? (
          <input type="text" style={s.editInputWide} value={shieldRides}
            placeholder="e.g. 6/4/2026" onChange={(e) => setShieldRides(e.target.value)} onBlur={saveShieldRides} />
        ) : (d.shield_rides || '—')}
      </td>
    </tr>
  );
}

const s = {
  tableScroll: { overflow: 'auto', maxHeight: 480 },
  table:       { width: '100%', borderCollapse: 'collapse', fontSize: t.caption.fontSize },
  tdDesc:      { ...tbl.td, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  tdNotes:     { ...tbl.td, minWidth: '140px' },
  empty:       { textAlign: 'center', padding: '24px', color: c.textFaint },
  editInput:   { width: '120px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
  editInputWide: { width: '100%', minWidth: '140px', padding: '4px 6px', border: `1px solid ${c.borderStrong}`, borderRadius: '4px', fontSize: t.caption.fontSize, fontFamily: 'inherit' },
};
